import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import dayjs from 'dayjs';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import { AppointmentStatusesOverviewCard, useHasAppointmentAnalyticsFeature } from '@frontend/analytics-appointment';
import {
  MessagingCategoriesOverviewCard,
  useIsMessagingAnalyticsDemoAccount,
  useMessagingAnalyticsDemoData,
  useMessagingAnalyticsShallowStore,
} from '@frontend/analytics-messaging';
import {
  PhoneIncomingCategoriesOverviewCard,
  useIsPhoneAnalyticsDemoAccount,
  usePhoneAnalyticsDemoData,
  usePhoneAnalyticsShallowStore,
} from '@frontend/analytics-phone';
import { GenerateAnalyticsReportCTA } from '@frontend/analytics-reporting';
import {
  AnalyticsHooks,
  AnalyticsUtils,
  GeneralFeedbackButton,
  useHasAutoRecallAnalyticsFeature,
} from '@frontend/analytics-shared';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { Header } from '@frontend/header-navigation';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { AutoRecallOnDashboard } from '../components/homepage-analytics/auto-recall-on-dashboard';
import { PracticeAnalyticsOnDashboard } from '../components/homepage-analytics/practice-analytics-on-dashboard';
import { useIsPAEligibleAccount } from '../components/practice/hooks/use-is-pa-eligible-account';
import { useHasRoiFeature } from '../components/roi/hooks/use-has-roi-feature';
import { NSRRoiBenchmarkTimelineChart } from '../components/roi/no-show-rate/charts/nsr-roi-benchmark-timeline-chart';
import { RoiOverviewCards } from '../components/roi/roi-overview-cards/roi-overview-cards';
import { featureFlags } from '../feature-flags';
import { trackingIds } from '../tracking-ids';

export const AnalyticsDashboard = () => {
  const { t } = useTranslation('analytics');
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();
  const [hasRoiCards, setHasRoiCards] = useState<boolean | undefined>();

  const { TimePeriodPopoverSelector, startDate, endDate } = AnalyticsHooks.useTimePeriodPopoverSelector({
    defaultPeriod: 'lastMonth',
  });
  const hasAppointmentAnalyticsFeature = useHasAppointmentAnalyticsFeature();
  const hasAutoRecallAnalyticsFeature = useHasAutoRecallAnalyticsFeature();
  const { isPAEligibleAccount } = useIsPAEligibleAccount();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const { preselectedAccessibleLocationIds } = AnalyticsHooks.useAnalyticsOrgLocations({
    isDemoAccount: getFeatureFlagValue(featureFlags.noShowRateRoiDemoData),
    module: 'ROI',
  });

  const { isFeatureEnabled: isNoShowRateRoiEnabled } = useHasRoiFeature({ roi: 'noShowRate' });

  const isPhoneAnalyticsDemoAccount = useIsPhoneAnalyticsDemoAccount();
  const phoneAnalyticsDemoData = usePhoneAnalyticsDemoData();
  const { setStore: setPhoneAnalyticsStore } = usePhoneAnalyticsShallowStore('setStore');

  const isMessagingAnalyticsDemoAccount = useIsMessagingAnalyticsDemoAccount();
  const messagingAnalyticsDemoData = useMessagingAnalyticsDemoData();
  const { setStore: setMessagingAnalyticsStore } = useMessagingAnalyticsShallowStore('setStore');

  const hasAnalyticsReportingAccess =
    AnalyticsHooks.useHasAdminAccess() || hasSchemaACL(selectedLocationIds[0], Permission.ANALYTICS_REPORTING_ACCESS);

  const [filters, setFilters] = useState<AnalyticsCommonTypes.AnalyticsSummaryFilters>({
    endDate: dayjs(endDate),
    startDate: dayjs(startDate),
    timeZone: accessibleLocationData[selectedLocationIds[0]]?.timezone,
  });

  const handleUpdateFilters = (filters: Partial<AnalyticsCommonTypes.AnalyticsSummaryFilters>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...filters,
    }));
  };

  useEffect(() => {
    handleUpdateFilters({
      startDate: dayjs(startDate),
      endDate: dayjs(endDate),
    });
  }, [startDate, endDate]);

  useEffect(() => {
    setPhoneAnalyticsStore({ demoData: phoneAnalyticsDemoData });
  }, [phoneAnalyticsDemoData, setPhoneAnalyticsStore]);

  useEffect(() => {
    setPhoneAnalyticsStore({ isDemoAccount: isPhoneAnalyticsDemoAccount });
  }, [isPhoneAnalyticsDemoAccount, setPhoneAnalyticsStore]);

  useEffect(() => {
    setMessagingAnalyticsStore({ demoData: messagingAnalyticsDemoData });
  }, [messagingAnalyticsDemoData, setMessagingAnalyticsStore]);

  useEffect(() => {
    setMessagingAnalyticsStore({ isDemoAccount: isMessagingAnalyticsDemoAccount });
  }, [isMessagingAnalyticsDemoAccount, setMessagingAnalyticsStore]);

  const lastMonthDates = AnalyticsUtils.findDatesRange({ offset: 1, range: 'month' });

  return (
    <Page>
      <Page.Header>
        <Page.Header.Heading css={styles.heading}>
          <Page.Header.Title title={<Header title={t('Analytics')} />} />
          <Page.Header.Action>
            <span css={styles.actions}>
              {hasAnalyticsReportingAccess && <GenerateAnalyticsReportCTA />}
              <GeneralFeedbackButton />
            </span>
          </Page.Header.Action>
        </Page.Header.Heading>
      </Page.Header>

      <Page.Body>
        <RoiOverviewCards
          setHasRoiCards={setHasRoiCards}
          style={{ marginBottom: theme.spacing(hasRoiCards ? 5 : 0) }}
        />
        <div style={{ marginBottom: theme.spacing(4) }}>
          <TimePeriodPopoverSelector trackingIdBase={trackingIds.analytics.dashboardTimeFilter} />
        </div>
        <div css={styles.chartsWrapper}>
          <PhoneIncomingCategoriesOverviewCard filters={filters} />
          {isNoShowRateRoiEnabled && (
            <NSRRoiBenchmarkTimelineChart
              filters={{
                ...lastMonthDates,
                locationIds: preselectedAccessibleLocationIds,
              }}
              isOnDashboard
            />
          )}
          <MessagingCategoriesOverviewCard filters={filters} />
          {isPAEligibleAccount && <PracticeAnalyticsOnDashboard filters={filters} />}
          {hasAutoRecallAnalyticsFeature && <AutoRecallOnDashboard filters={filters} />}
          {hasAppointmentAnalyticsFeature && <AppointmentStatusesOverviewCard filters={filters} />}
        </div>
      </Page.Body>
    </Page>
  );
};

const styles = {
  chartsWrapper: css`
    column-gap: ${theme.spacing(3)};
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;

    > section {
      flex-grow: 1;
    }

    @container (min-width: ${breakpoints.medium.max}px) {
      flex-direction: row;

      > section {
        width: calc(50% - ${theme.spacing(1.5)});
      }
    }
  `,

  heading: css`
    display: flex;
    justify-content: space-between;

    @media screen and (max-width: ${breakpoints.xsmall.max}px) {
      margin-bottom: ${theme.spacing(2)};
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
};
